import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import ScrollCta from '@/components/base/scroll-cta'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

type HeroType =
  | 'project'
  | 'journal'
  | 'sales-network'
  | 'store-details'
  | 'contact'

type HeroDetail = {
  label: string
  value: string
}

export type HeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    type: HeroType
    pretitle: string
    title: string
    text: string
    details: HeroDetail[]
    image: Partial<WPImage>
    link: Partial<WPLink>
  }>

const TITLES: Record<HeroType, string> = {
  project: 'Project',
  journal: 'Journal',
  'sales-network': 'Sales Network',
  'store-details': 'Store Details',
  contact: 'Contact'
}

const Hero = ({
  type = 'project',
  pretitle,
  title,
  text,
  details,
  className,
  ...props
}: HeroProps) => {
  if (type === 'project') {
    return (
      <Section className={cn(className)} {...props}>
        <Spacer variant="xxl" theme="#00000000" />
        <Container className="relative">
          <Grid className="gap-y-spacing-sm">
            <div className="col-span-2 md:col-span-6 lg:col-span-12">
              <Typography variant="h2" component="h1">
                {title || TITLES[type]}
              </Typography>
            </div>

            <div className="relative col-span-2 flex md:col-span-6 lg:col-span-12">
              <Typography
                variant="large"
                className="text-text-primary-light dark:text-text-primary-dark"
              >
                {text}
              </Typography>
              <div className="absolute bottom-spacing-md right-0 hidden md:block lg:bottom-spacing-2xs lg:right-0">
                <ScrollCta />
              </div>
            </div>
          </Grid>
        </Container>
        <Spacer variant="md" theme="#00000000" />
      </Section>
    )
  }

  if (type === 'journal') {
    const dateValue = details?.[0]?.value || pretitle
    const categoryValue = details?.[1]?.value || text

    return (
      <Section
        className={cn('text-heading-light dark:text-heading-dark', className)}
        {...props}
      >
        <Spacer variant="xxl" theme="#00000000" />
        <Container className="">
          <Grid className="items-baseline gap-y-spacing-sm">
            <div className="col-span-2 md:col-span-6 lg:col-span-9">
              <Typography variant="h2" component="h1">
                {title || TITLES[type]}
              </Typography>
            </div>

            <div className="col-span-2 md:col-span-6 lg:col-span-3">
              <div className="flex items-center gap-spacing-sm text-text-primary-light lg:justify-end dark:text-text-primary-dark">
                {dateValue ? (
                  <Typography variant="large">{dateValue}</Typography>
                ) : null}
                {categoryValue ? (
                  <Typography variant="large">{categoryValue}</Typography>
                ) : null}
              </div>
            </div>
          </Grid>
        </Container>
        <Spacer variant="md" theme="#00000000" />
      </Section>
    )
  }

  if (type === 'sales-network') {
    return (
      <Section
        className={cn('text-heading-light dark:text-heading-dark', className)}
        {...props}
      >
        <Spacer variant="xxl" theme="#00000000" />
        <Container className="">
          <Grid className="items-baseline gap-y-spacing-sm">
            <div className="col-span-2 md:col-span-6 lg:col-span-7">
              <Typography variant="h2" component="h1">
                {title || TITLES[type]}
              </Typography>
            </div>
          </Grid>
        </Container>
        <Spacer variant="md" theme="#00000000" />
      </Section>
    )
  }

  return (
    <Section
      className={cn('text-heading-light dark:text-heading-dark', className)}
      {...props}
    >
      <Spacer variant="xxl" theme="#00000000" />
      <Container className="">
        <Grid className="items-baseline gap-y-spacing-sm">
          <div className="col-span-2 md:col-span-6 lg:col-span-7">
            <Typography variant="h2" component="h1">
              {title || TITLES[type]}
            </Typography>
          </div>
        </Grid>
      </Container>
      <Spacer variant="md" theme="#00000000" />
    </Section>
  )
}

export default Hero
